// src/belediye/pages/sistem/SistemIdariSinirlarPage.js
//
// FAZ-24 BLOK-A.3 — İdari Sınırlar yönetim ekranı: liste (level/q filtresi, pasif
// göster) + ekle/düzenle (level→parent→mahalle mantığı + geojson geometry + stil)
// + soft-delete + GeoJSON dışa/içe aktar (import gövdesi JSON, FormData DEĞİL).
// Gate: admin_boundaries.view (route), .create/.update/.delete/.import/.export (aksiyon).

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { PlusIcon, MagnifyingGlassIcon, ArrowUpTrayIcon, ArrowDownTrayIcon, MapIcon } from "@heroicons/react/24/outline";

import usePermissions from "../../hooks/usePermissions";
import {
  fetchBoundaries,
  fetchBoundary,
  createBoundary,
  updateBoundary,
  deleteBoundary,
  exportBoundaries,
  importBoundaries,
  fetchNeighborhoods,
} from "../../services/yonetimApi";
import "./yonetim-ekrani.css";

const LEVELS = [
  { value: "il", label: "İl" },
  { value: "ilce", label: "İlçe" },
  { value: "mahalle", label: "Mahalle" },
];
const LEVEL_LABEL = { il: "İl", ilce: "İlçe", mahalle: "Mahalle" };
const PARENT_LEVEL = { il: null, ilce: "il", mahalle: "ilce" };

const backendError = (ex, fallback) => {
  const d = ex?.response?.data;
  if (typeof d === "string") return d;
  if (d?.detail) return d.detail;
  if (d) {
    return Object.entries(d)
      .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") : v}`)
      .join(" · ");
  }
  return fallback;
};

export default function SistemIdariSinirlarPage() {
  const { can } = usePermissions();
  const canCreate = can("admin_boundaries.create");
  const canExport = can("admin_boundaries.export");
  const canImport = can("admin_boundaries.import");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");
  const [level, setLevel] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [editing, setEditing] = useState(null); // null | {} (yeni) | boundary (düzenle)
  const [importResult, setImportResult] = useState(null);
  const [busyMsg, setBusyMsg] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchBoundaries({
        level: level || undefined,
        q: q.trim() || undefined,
        only_active: showInactive ? "false" : "true",
        with_geojson: "false",
      });
      setRows(Array.isArray(data?.results) ? data.results : []);
    } catch {
      setError("İdari sınırlar yüklenemedi.");
    } finally {
      setLoading(false);
    }
  }, [level, q, showInactive]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const doExport = async () => {
    setBusyMsg("Dışa aktarılıyor…");
    try {
      const data = await exportBoundaries();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/geo+json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "idari-sinirlar.geojson";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (ex) {
      setError(backendError(ex, "Dışa aktarılamadı."));
    } finally {
      setBusyMsg("");
    }
  };

  return (
    <div className="page-container ye-root">
      <header className="ye-head">
        <div>
          <h1 className="ye-title">İdari Sınırlar</h1>
          <p className="ye-subtitle">
            İl / ilçe / mahalle sınır katmanlarını, stillerini ve GeoJSON
            geometrilerini yönetin.
          </p>
        </div>
        <div className="ye-head-actions">
          {canExport && (
            <button className="btn-md btn-md-ghost" onClick={doExport} disabled={!!busyMsg}>
              <ArrowDownTrayIcon width={18} height={18} /> Dışa aktar
            </button>
          )}
          {canImport && <ImportButton onDone={(res) => { setImportResult(res); load(); }} onError={setError} />}
          {canCreate && (
            <button className="btn-md btn-md-primary ye-new-btn" onClick={() => setEditing({})}>
              <PlusIcon width={18} height={18} /> Yeni Sınır
            </button>
          )}
        </div>
      </header>

      {importResult && (
        <div className={`ye-alert ${importResult.error_count ? "" : "ye-alert-ok"}`}>
          İçe aktarma: {importResult.created} eklendi, {importResult.updated} güncellendi
          {importResult.error_count ? `, ${importResult.error_count} hata` : ""}.
          {Array.isArray(importResult.errors) && importResult.errors.length > 0 && (
            <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
              {importResult.errors.slice(0, 5).map((e, i) => (
                <li key={i}>#{e.index}: {JSON.stringify(e.errors)}</li>
              ))}
            </ul>
          )}
          <div style={{ marginTop: 6 }}>
            <button className="btn-md btn-md-ghost" onClick={() => setImportResult(null)}>Kapat</button>
          </div>
        </div>
      )}

      <div className="ye-filters">
        <div className="ye-search">
          <MagnifyingGlassIcon width={18} height={18} className="ye-search-icon" />
          <input
            className="md-input ye-search-input"
            placeholder="Sınır / üst / mahalle adı ara"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            aria-label="Sınır ara"
          />
        </div>
        <select className="md-select ye-filter-select" value={level} onChange={(e) => setLevel(e.target.value)} aria-label="Seviye">
          <option value="">Tüm seviyeler</option>
          {LEVELS.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
        </select>
        <label className="btn-md btn-md-ghost" style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
          Pasifleri göster
        </label>
      </div>

      {busyMsg && <div className="ye-state">{busyMsg}</div>}

      {loading ? (
        <div className="ye-state">Yükleniyor…</div>
      ) : error ? (
        <div className="ye-state ye-state-error">
          {error}
          <button className="btn-md btn-md-ghost" onClick={load}>Tekrar dene</button>
        </div>
      ) : rows.length === 0 ? (
        <div className="ye-state">Kayıt bulunamadı.</div>
      ) : (
        <ul className="ye-list">
          {rows.map((b) => (
            <li key={b.id}>
              <button className="md-card ye-item" onClick={() => setEditing(b)}>
                <span className="ye-item-main">
                  <span className="ye-item-name">
                    <MapIcon width={15} height={15} style={{ verticalAlign: "-2px", marginRight: 6 }} />
                    {b.name}
                  </span>
                  <span className="ye-item-sub">
                    {b.level_display || LEVEL_LABEL[b.level]}
                    {b.parent_name ? ` · ↑ ${b.parent_name}` : ""}
                    {b.neighborhood_name ? ` · ${b.neighborhood_name}` : ""}
                  </span>
                </span>
                <span className="ye-item-meta">
                  {b.is_default_view && <span className="md-chip">Varsayılan</span>}
                  {b.has_geojson ? <span className="md-chip md-chip-success">Geometri</span> : <span className="md-chip">Geometri yok</span>}
                  {b.child_count > 0 && <span className="md-chip">{b.child_count} alt</span>}
                  {!b.is_active && <span className="md-chip md-chip-danger">Pasif</span>}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {editing !== null && (
        <BoundaryModal
          boundary={editing.id ? editing : null}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function ImportButton({ onDone, onError }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // aynı dosya tekrar seçilebilsin
    if (!file) return;
    setBusy(true);
    try {
      const text = await file.text();
      let body;
      try {
        body = JSON.parse(text);
      } catch {
        onError("Geçersiz JSON dosyası.");
        return;
      }
      const res = await importBoundaries(body);
      onDone(res);
    } catch (ex) {
      onError(backendError(ex, "İçe aktarılamadı."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button className="btn-md btn-md-ghost" onClick={() => inputRef.current?.click()} disabled={busy}>
        <ArrowUpTrayIcon width={18} height={18} /> {busy ? "Yükleniyor…" : "İçe aktar"}
      </button>
      <input ref={inputRef} type="file" accept=".json,.geojson,application/json,application/geo+json"
        onChange={onPick} style={{ display: "none" }} />
    </>
  );
}

const emptyForm = {
  name: "", level: "il", parent: "", neighborhood: "", geojson: "",
  center_lat: "", center_lng: "", population: "", area_km2: "",
  display_mode: "both", fill_opacity: 0.12, border_color: "#1f2a40",
  fill_color: "#314a6b", stroke_width: 2, zoom_level: 13, sort_order: 0,
  is_default_view: false, is_active: true,
};

// "" / virgüllü ondalık / geçersiz → güvenli sayı dönüşümü (REVIEW #4)
const toNum = (v) => {
  if (v === "" || v === null || v === undefined) return null;
  const n = Number(String(v).replace(",", "."));
  return Number.isNaN(n) ? null : n;
};
// boş bırakılınca varsayılana düş (REVIEW #7: Number("")=0 → backend 400)
const toNumDef = (v, def) => {
  const n = toNum(v);
  return n === null ? def : n;
};

function BoundaryModal({ boundary, onClose, onSaved }) {
  const { can } = usePermissions();
  const isEdit = !!boundary;
  const canUpdate = can("admin_boundaries.update");
  const canDelete = isEdit && can("admin_boundaries.delete");

  const [form, setForm] = useState(() =>
    boundary
      ? {
          ...emptyForm,
          ...boundary,
          parent: boundary.parent ?? "",
          neighborhood: boundary.neighborhood ?? "",
          geojson: boundary.geojson ? JSON.stringify(boundary.geojson, null, 2) : "",
          center_lat: boundary.center_lat ?? "",
          center_lng: boundary.center_lng ?? "",
          population: boundary.population ?? "",
          area_km2: boundary.area_km2 ?? "",
        }
      : { ...emptyForm }
  );
  const [allBoundaries, setAllBoundaries] = useState([]);
  const [neighborhoods, setNeighborhoods] = useState([]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  // REVIEW #1: liste with_geojson=false → satırda geometri YOK. Düzenlemede detayı
  // çekip textarea'yı doldurmadan Kaydet, geojson'ı null'lar (veri kaybı). geojsonLoaded
  // false kaldıkça (create hariç / fetch başarısız) payload'dan geojson ÇIKARILIR.
  const [geojsonLoaded, setGeojsonLoaded] = useState(!boundary);

  const set = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  // REVIEW #3: seviye değişince üst/mahalle bayat kalmasın → sıfırla.
  const onLevelChange = (e) => {
    const level = e.target.value;
    setForm((f) => ({ ...f, level, parent: "", neighborhood: "" }));
  };

  useEffect(() => {
    fetchBoundaries({ only_active: "true", with_geojson: "false" })
      .then((d) => setAllBoundaries(Array.isArray(d?.results) ? d.results : []))
      .catch(() => {});
    fetchNeighborhoods().then(setNeighborhoods).catch(() => {});
    // Düzenleme: gerçek geometriyi detay ucundan yükle (liste taşımaz)
    if (boundary?.id) {
      fetchBoundary(boundary.id, { include_children: "false" })
        .then((d) => {
          setForm((f) => ({ ...f, geojson: d?.geojson ? JSON.stringify(d.geojson, null, 2) : "" }));
          setGeojsonLoaded(true);
        })
        .catch(() => {}); // yüklenemedi → geojsonLoaded false → submit'te geojson atlanır
    }
  }, [boundary]);

  const parentLevel = PARENT_LEVEL[form.level];
  const parentOptions = useMemo(
    () => allBoundaries.filter((b) => b.level === parentLevel && b.id !== boundary?.id),
    [allBoundaries, parentLevel, boundary]
  );

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    // geojson metnini geometry objesine çevir
    let geojsonVal = null;
    if (String(form.geojson).trim()) {
      try {
        geojsonVal = JSON.parse(form.geojson);
      } catch {
        setErr("GeoJSON alanı geçerli JSON değil.");
        return;
      }
    }
    const payload = {
      name: form.name,
      level: form.level,
      parent: form.parent === "" ? null : Number(form.parent),
      neighborhood: form.level === "mahalle" && form.neighborhood !== "" ? Number(form.neighborhood) : null,
      geojson: geojsonVal,
      center_lat: toNum(form.center_lat),
      center_lng: toNum(form.center_lng),
      population: toNum(form.population),
      area_km2: toNum(form.area_km2),
      display_mode: form.display_mode,
      fill_opacity: toNumDef(form.fill_opacity, 0.12),
      border_color: form.border_color,
      fill_color: form.fill_color,
      stroke_width: toNumDef(form.stroke_width, 2),
      zoom_level: toNumDef(form.zoom_level, 13),
      sort_order: toNumDef(form.sort_order, 0),
      is_default_view: !!form.is_default_view,
      is_active: !!form.is_active,
    };
    // REVIEW #1: detay geojson yüklenmediyse (fetch başarısız) geometriye DOKUNMA
    if (isEdit && !geojsonLoaded) delete payload.geojson;
    setSaving(true);
    try {
      if (isEdit) await updateBoundary(boundary.id, payload);
      else await createBoundary(payload);
      onSaved();
    } catch (ex) {
      setErr(backendError(ex, "Sınır kaydedilemedi."));
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    setSaving(true);
    setErr("");
    try {
      await deleteBoundary(boundary.id);
      onSaved();
    } catch (ex) {
      setErr(backendError(ex, "Sınır silinemedi."));
      setSaving(false);
    }
  };

  return (
    <div className="ye-modal-overlay" onClick={onClose} role="presentation">
      <div className="ye-modal ye-modal-wide md-card" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="md-card-header">
          <h2 className="md-card-title">{isEdit ? "Sınırı Düzenle" : "Yeni Sınır"}</h2>
        </div>
        <form className="ye-form" onSubmit={submit} autoComplete="off">
          {err && <div className="ye-alert">{err}</div>}

          <div className="ye-field-row">
            <label className="ye-field"><span>Seviye</span>
              <select className="md-select" value={form.level} onChange={onLevelChange}>
                {LEVELS.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
              </select>
            </label>
            <label className="ye-field"><span>Ad</span>
              <input className="md-input" value={form.name} onChange={set("name")}
                placeholder={form.level === "mahalle" ? "(boşsa mahalleden dolar)" : "Sınır adı"}
                required={form.level !== "mahalle"} />
            </label>
          </div>

          {parentLevel && (
            <label className="ye-field"><span>Üst sınır ({LEVEL_LABEL[parentLevel]})</span>
              <select className="md-select" value={form.parent} onChange={set("parent")} required>
                <option value="">Seçiniz…</option>
                {parentOptions.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </label>
          )}

          {form.level === "mahalle" && (
            <label className="ye-field"><span>Mahalle (Neighborhood)</span>
              <select className="md-select" value={form.neighborhood} onChange={set("neighborhood")} required>
                <option value="">Seçiniz…</option>
                {neighborhoods.map((n) => <option key={n.id} value={n.id}>{n.name}</option>)}
              </select>
            </label>
          )}

          <label className="ye-field"><span>GeoJSON geometrisi (Polygon/MultiPolygon…) — opsiyonel</span>
            <textarea className="md-input" value={form.geojson} onChange={set("geojson")} rows={5}
              placeholder='{"type":"Polygon","coordinates":[[[...]]]}' />
          </label>

          <div className="ye-field-row">
            <label className="ye-field"><span>Merkez enlem</span>
              <input className="md-input" value={form.center_lat} onChange={set("center_lat")} inputMode="decimal" /></label>
            <label className="ye-field"><span>Merkez boylam</span>
              <input className="md-input" value={form.center_lng} onChange={set("center_lng")} inputMode="decimal" /></label>
            <label className="ye-field"><span>Zoom (1–20)</span>
              <input className="md-input" type="number" min={1} max={20} value={form.zoom_level} onChange={set("zoom_level")} /></label>
          </div>

          <div className="ye-field-row">
            <label className="ye-field"><span>Nüfus</span>
              <input className="md-input" type="number" min={0} value={form.population} onChange={set("population")} /></label>
            <label className="ye-field"><span>Alan (km²)</span>
              <input className="md-input" value={form.area_km2} onChange={set("area_km2")} inputMode="decimal" /></label>
            <label className="ye-field"><span>Sıra</span>
              <input className="md-input" type="number" min={0} value={form.sort_order} onChange={set("sort_order")} /></label>
          </div>

          <div className="ye-field-row">
            <label className="ye-field"><span>Görünüm</span>
              <select className="md-select" value={form.display_mode} onChange={set("display_mode")}>
                <option value="both">Çizgi + dolgu</option>
                <option value="line">Yalnız çizgi</option>
                <option value="fill">Yalnız dolgu</option>
              </select></label>
            <label className="ye-field"><span>Kenar rengi</span>
              <input className="md-input" type="color" value={form.border_color} onChange={set("border_color")} /></label>
            <label className="ye-field"><span>Dolgu rengi</span>
              <input className="md-input" type="color" value={form.fill_color} onChange={set("fill_color")} /></label>
          </div>

          <div className="ye-field-row">
            <label className="ye-field"><span>Dolgu opaklığı (0–1)</span>
              <input className="md-input" type="number" min={0} max={1} step={0.01} value={form.fill_opacity} onChange={set("fill_opacity")} /></label>
            <label className="ye-field"><span>Çizgi kalınlığı (1–12)</span>
              <input className="md-input" type="number" min={1} max={12} value={form.stroke_width} onChange={set("stroke_width")} /></label>
          </div>

          <div className="ye-field-row">
            <label className="ye-field" style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
              <input type="checkbox" checked={!!form.is_default_view} onChange={set("is_default_view")} />
              <span>Varsayılan görünüm</span>
            </label>
            {isEdit && (
              <label className="ye-field" style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
                <input type="checkbox" checked={!!form.is_active} onChange={set("is_active")} />
                <span>Aktif</span>
              </label>
            )}
          </div>

          {confirmDelete ? (
            <div className="ye-alert">
              Bu sınır pasifleştirilsin mi (soft-delete)? Alt sınırlar korunur.
              <div className="ye-modal-actions">
                <button type="button" className="btn-md btn-md-ghost" onClick={() => setConfirmDelete(false)} disabled={saving}>Vazgeç</button>
                <button type="button" className="btn-md btn-md-danger" onClick={doDelete} disabled={saving}>
                  {saving ? "Siliniyor…" : "Evet, sil"}
                </button>
              </div>
            </div>
          ) : (
            <div className="ye-modal-actions ye-modal-actions-split">
              {canDelete ? (
                <button type="button" className="btn-md btn-md-ghost ye-danger-text" onClick={() => setConfirmDelete(true)} disabled={saving}>Sil</button>
              ) : <span />}
              <div className="ye-modal-actions">
                <button type="button" className="btn-md btn-md-ghost" onClick={onClose} disabled={saving}>
                  {isEdit && !canUpdate ? "Kapat" : "Vazgeç"}
                </button>
                {(!isEdit || canUpdate) && (
                  <button type="submit" className="btn-md btn-md-primary" disabled={saving}>
                    {saving ? "Kaydediliyor…" : isEdit ? "Kaydet" : "Oluştur"}
                  </button>
                )}
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
